import {CopilotSpaceSelectionProvider} from '@github-ui/copilot-chat-contexts/CopilotSpaceSelectionContext'
import {
  ObservableChatStateProvider,
  useObservableChatStateContext,
} from '@github-ui/copilot-chat-contexts/ObservableChatContext'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {buildCopilotChatInitialState} from '@github-ui/copilot-chat-state/copilot-chat-initial-state'
import type {CopilotChatAction, CopilotChatState, Dispatcher} from '@github-ui/copilot-chat-state/copilot-chat-reducer'
import {copilotChatReducer} from '@github-ui/copilot-chat-state/copilot-chat-reducer'
import type {
  CopilotChatMessage,
  CopilotChatMode,
  CopilotChatOrg,
  CopilotChatPayload,
  CopilotChatReference,
  CopilotChatRepo,
  CopilotSpace,
} from '@github-ui/copilot-chat-types'
import {EntitlementProvider} from '@github-ui/copilot-entitlement/EntitlementContext'
import {useDerivedObservable, useObservableValueRef, useObservedState} from '@github-ui/react-observable'
import type {PropsWithChildren} from 'react'
import {createContext, use, useCallback, useEffect, useLayoutEffect, useReducer, useRef} from 'react'

import {useRestoredChatState, useSaveChatState} from '../hooks/use-restore-chat-state'
import {announceReferenceChange} from './copilot-chat-announcements'
import {emitChatTelemetry} from './copilot-chat-telemetry'
import {CopilotChatManagerProvider} from './CopilotChatManagerContext'

const CopilotChatDispatchContext = createContext<React.Dispatch<CopilotChatAction> | null>(null)

export interface CopilotChatProviderProps {
  topic?: CopilotChatRepo
  threadId: string | null
  workerPath: string
  refs: CopilotChatReference[]
  selectedReference?: CopilotChatReference | null
  mode: CopilotChatMode
  ssoOrganizations: CopilotChatOrg[]
  chatIsOpen?: boolean
  chatIsVisible?: boolean
  chatVisibleSettingPath?: string
  customCopilots?: CopilotSpace[]
  messages?: CopilotChatMessage[]
  testReducerState?: CopilotChatState
  realIp?: string
  currentView?: 'thread' | 'list'
  copilotChatPayload: CopilotChatPayload
}

export function CopilotChatProvider({
  children,
  topic,
  workerPath,
  threadId,
  refs,
  selectedReference,
  mode,
  ssoOrganizations,
  chatIsOpen,
  chatIsVisible,
  chatVisibleSettingPath,
  customCopilots,
  messages,
  testReducerState,
  realIp,
  currentView,
  copilotChatPayload,
}: PropsWithChildren<CopilotChatProviderProps>) {
  const restoredChatState = useRestoredChatState(threadId, mode)
  const initialState = buildCopilotChatInitialState({
    topic,
    threadId,
    workerPath,
    refs,
    selectedReference,
    mode,
    ssoOrganizations,
    chatIsOpen,
    chatIsVisible,
    chatVisibleSettingPath,
    customCopilots,
    messages,
    testReducerState,
    currentView,
    copilotChatPayload,
    restoredMessages: restoredChatState.restoredMessages,
    restoredThreadTitle: restoredChatState.restoredThreadTitle,
  })

  const [state, baseDispatch] = useReducer(copilotChatReducer, initialState)
  // Track the latest committed state so the dispatch boundary can read the pre-dispatch state
  // when emitting side effects (announcements, telemetry) without making them depend on `state`.
  const stateRef = useRef(state)
  useLayoutEffect(() => {
    stateRef.current = state
  }, [state])
  // Screen-reader announcements and analytics are side effects, so they run here at the dispatch
  // boundary rather than inside the (pure) reducer. They are best-effort and must never block the
  // state update, so a throw in either one is swallowed and the reducer dispatch still runs.
  const dispatch = useCallback<Dispatcher>(
    action => {
      try {
        announceReferenceChange(action)
      } catch {
        // best-effort: a failed announcement must not block the state update
      }
      try {
        emitChatTelemetry(stateRef.current, action)
      } catch {
        // best-effort: failed analytics must not block the state update
      }
      baseDispatch(action)
    },
    [baseDispatch],
  )
  useSaveChatState(state.messages, state.selectedThreadID, state.threads, state.mode)

  useEffect(
    function saveCurrentReferences() {
      if (!state.chatIsOpen) return

      const referencesWithoutCurrentRepo = state.currentReferences.filter(
        r => r.type !== 'repository' || r.id !== state.currentRepository?.id,
      )
      copilotLocalStorage.setCurrentReferences(state.selectedThreadID, referencesWithoutCurrentRepo)
    },
    [state.selectedThreadID, state.currentReferences, state.currentRepository, state.chatIsOpen],
  )

  return (
    <EntitlementProvider
      initialLicenseType={copilotChatPayload.licenseType}
      initialPlan={copilotChatPayload.plan}
      initialQuotas={copilotChatPayload.quotas}
      hasCEorCBAccess={copilotChatPayload.hasCEorCBAccess}
      initialCanUpgradePlan={copilotChatPayload.canUpgradePlan}
    >
      <ChatStateProvider state={state}>
        <CopilotChatDispatchContext value={dispatch}>
          <CopilotChatManagerProvider
            apiURL={copilotChatPayload.apiURL}
            apiVersion={copilotChatPayload.apiVersion}
            state={state}
            dispatch={dispatch}
            ssoOrganizations={ssoOrganizations}
            realIp={realIp}
            hasCEorCBAccess={copilotChatPayload.hasCEorCBAccess}
          >
            <CopilotSpaceSelectionProvider selectedThreadId={state.selectedThreadID}>
              {children}
            </CopilotSpaceSelectionProvider>
          </CopilotChatManagerProvider>
        </CopilotChatDispatchContext>
      </ChatStateProvider>
    </EntitlementProvider>
  )
}

const ChatStateContext = createContext<CopilotChatState | null>(null)

export function ChatStateProvider({state, children}: PropsWithChildren<{state: CopilotChatState}>) {
  // A constant reference to an Observable
  const [stateObservable, setStateObservable] = useObservableValueRef(state)

  // When the state updates, update the value of the observable
  useLayoutEffect(() => {
    if (state !== stateObservable.current.value) {
      setStateObservable(state)
    }
  }, [state, stateObservable, setStateObservable])

  return (
    <ObservableChatStateProvider value={stateObservable}>
      <ChatStateContext value={state}>{children}</ChatStateContext>
    </ObservableChatStateProvider>
  )
}

/**
 * Returns the current version of the chat state.
 * This causes the component to re-render any time there is an update to any part of the state.
 * Our goal is to deprecate and remove this hook.
 *
 * @example
 * ```ts
 * const {messages, customInstructions} = useChatState()
 * ```
 */
export function useChatState(): CopilotChatState {
  const chatState = use(ChatStateContext)
  if (!chatState) throw new Error('useChatState can only be used inside a CopilotChatProvider')
  return chatState
}

/**
 * Returns the current version of the chat state or undefined if not within a CopilotChatProvider.
 * This is a safe version of useChatState that won't throw an error when used outside
 * of a chat context. Useful for components that may or may not be rendered within
 * Copilot Chat.
 *
 * @example
 * ```ts
 * const chatState = useSafeChatState()
 * const isImmersiveChat = chatState?.mode === 'immersive'
 * ```
 */
export function useSafeChatState(): CopilotChatState | undefined {
  const chatState = use(ChatStateContext)
  return chatState ?? undefined
}

/**
 * Returns the result of the lens function applied to the chat state.
 * This causes the component to re-render whenever the given function results in a new value.
 * The lens function must always be pure.
 *
 * @example
 * ```ts
 * const hasMessages = useChatStateLens(s => s.messages.length > 0)
 * ```
 */
export function useChatStateLens<T>(lens: (state: CopilotChatState) => T): T {
  const o = useObservableChatStateContext()
  if (!o || !o.current) throw new Error('useChatStateLens can only be used inside a CopilotChatProvider')
  const valueObservable = useDerivedObservable(o.current, lens)
  return useObservedState(valueObservable)
}

/**
 * Returns the current value of `state[key]` on the `CopilotChatState`.
 * This causes the component to re-render whenever that property has a new value.
 *
 * @example
 * ```ts
 * const model = useChatStateValue('model')
 * ```
 */
export function useChatStateValue<T extends keyof CopilotChatState>(key: T): CopilotChatState[T] {
  return useChatStateLens(s => s[key])
}

/**
 * Returns a subset of the `CopilotChatState` containing only the specified keys.
 * This causes the component to re-render whenever a property corresponding to one of those keys has a new value.
 *
 * @example
 * ```ts
 * const {model, availableModels} = useChatStateValues('model', 'availableModels')
 * ```
 */
export function useChatStateValues<TKey extends keyof CopilotChatState = never>(
  ...keys: TKey[]
): Pick<CopilotChatState, TKey> {
  return useChatStateLens(s => getSubsetFromState(s, keys))
}

export function getSubsetFromState<TObject extends object, TKey extends keyof TObject>(
  state: TObject,
  keys: TKey[],
): Pick<TObject, TKey> {
  const out = {} as unknown as Pick<TObject, TKey>
  for (const key of keys) {
    out[key] = state[key]
  }
  return out
}

export function useChatDispatch(): React.Dispatch<CopilotChatAction> {
  const dispatch = use(CopilotChatDispatchContext)
  if (!dispatch) throw new Error('useChatDispatch can only be used inside a CopilotChatProvider')
  return dispatch
}
